// Módulo puro (sem Angular e sem enums): roda também sob node --experimental-strip-types nos testes.

export interface KeySink {
    keyDown(keyCode: number): void;
    keyUp(keyCode: number): void;
    keyUnicodeDown(codePoint: number): void;
    keyUnicodeUp(codePoint: number): void;
}

export type SendStep =
    | { kind: 'down'; code: number }
    | { kind: 'up'; code: number }
    | { kind: 'unicode'; codePoint: number };

export interface PlanExecution {
    cancel(): void;
    readonly done: Promise<void>;
}

export type Scheduler = (fn: () => void, delayMs: number) => unknown;

// mesma cadência dos setTimeout(25) do pipeline do teclado físico (session-view-model.ts)
export const STEP_DELAY_MS = 25;

const ENTER_KEY_CODE = 13;

export function buildTextPlan(text: string): SendStep[] {
    const steps: SendStep[] = [];
    for (const char of text) {
        if (char === '\r') {
            continue;
        }
        if (char === '\n') {
            steps.push({ kind: 'down', code: ENTER_KEY_CODE });
            steps.push({ kind: 'up', code: ENTER_KEY_CODE });
        } else {
            steps.push({ kind: 'unicode', codePoint: char.codePointAt(0) ?? 0 });
        }
    }
    return steps;
}

export function buildKeyComboPlan(keyCodes: number[]): SendStep[] {
    const downs: SendStep[] = keyCodes.map(code => ({ kind: 'down', code }));
    const ups: SendStep[] = [...keyCodes].reverse().map(code => ({ kind: 'up', code }));
    return [...downs, ...ups];
}

export function executePlan(plan: SendStep[], sink: KeySink, scheduler: Scheduler = (fn, delayMs) => setTimeout(fn, delayMs)): PlanExecution {
    const pressed: number[] = [];
    let finished = false;
    let index = 0;
    let resolveDone!: () => void;
    const done = new Promise<void>(resolve => {
        resolveDone = resolve;
    });

    const releasePressed = (): void => {
        for (let i = pressed.length - 1; i >= 0; i--) {
            sink.keyUp(pressed[i]);
        }
        pressed.length = 0;
    };

    const finish = (): void => {
        if (finished) {
            return;
        }
        finished = true;
        releasePressed();
        resolveDone();
    };

    const runStep = (): void => {
        if (finished) {
            return;
        }
        if (index >= plan.length) {
            finish();
            return;
        }
        const step = plan[index++];
        if (step.kind === 'down') {
            sink.keyDown(step.code);
            pressed.push(step.code);
        } else if (step.kind === 'up') {
            sink.keyUp(step.code);
            const i = pressed.lastIndexOf(step.code);
            if (i > -1) {
                pressed.splice(i, 1);
            }
        } else {
            sink.keyUnicodeDown(step.codePoint);
            sink.keyUnicodeUp(step.codePoint);
        }
        if (index >= plan.length) {
            finish();
            return;
        }
        scheduler(runStep, STEP_DELAY_MS);
    };

    runStep();

    return {
        cancel: finish,
        done,
    };
}
